import { buildLRUCache } from '../lib/cache/LRUCache';
import { SiteClassification } from '../types/api/types';
import { getClassificationsStore } from './stores';

const classificationLRU = buildLRUCache<string, SiteClassification>(100);
const classificationMissesLRU = buildLRUCache<string, number>(250);

export const updateCache = () => {
  getClassificationsStore().set({
    classifications: classificationLRU.toJSON(),
    classificationMisses: classificationMissesLRU.toJSON(),
  });
};

export const loadLRUFromCache = () => {
  const data = getClassificationsStore().get();
  if (!data) {
    return;
  }

  classificationLRU.init(data.classifications);
  classificationMissesLRU.init(data.classificationMisses ?? []);
};

export const getCachedClassification = (hostname: string) =>
  classificationLRU.get(hostname);

export const setCachedClassification = (
  hostname: string,
  value: SiteClassification
) => {
  classificationLRU.set(hostname, value);
  classificationMissesLRU.deleteByKey(hostname);
  updateCache();
};

/**
 * Determines whether a cached 404 should suppress another classification call.
 *
 * Expired misses are removed eagerly so they cannot survive beyond their TTL.
 */
export const hasCachedClassificationMiss = (
  hostname: string,
  nowMs: number
): boolean => {
  const expiresAtMs = classificationMissesLRU.get(hostname);
  if (expiresAtMs === undefined) {
    return false;
  }

  if (expiresAtMs > nowMs) {
    return true;
  }

  classificationMissesLRU.deleteByKey(hostname);
  updateCache();
  return false;
};

/**
 * Records a short-lived classification miss unless a positive result won a
 * concurrent request for the same hostname.
 */
export const setCachedClassificationMiss = (
  hostname: string,
  expiresAtMs: number
): void => {
  if (classificationLRU.get(hostname)) {
    return;
  }

  classificationMissesLRU.set(hostname, expiresAtMs);
  updateCache();
};

export const clearCachedClassification = () => {
  classificationLRU.clear();
  classificationMissesLRU.clear();
  updateCache();
};
